import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import {
  PaymentsPayinService,
  PayinRail,
  PayinRuntimeStatus,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import {
  markAnnounced,
  screenTokens,
  showOnScreen,
} from "@/shared/dashboard-alerts/dashboardAlertAnnouncements";
import { announcementIdFor } from "@/shared/dashboard-alerts/dashboardAlertEvents";
import type { BrebReceivingKey } from "@/shared/services/payments/brebReceivingKey.service";

import {
  type BrebKeyIncomingPayment,
  incomingPaymentFor,
  isIncomingPaymentCandidate,
  withIncomingPayment,
} from "./brebKeyIncomingPayments";

/** Fallback cadence while the QR is open, for when the socket dropped the push. */
const INCOMING_POLL_MS = 10_000;
/** Newest credited Bre-B payins read per poll. Far more than arrive in one interval. */
const INCOMING_POLL_LIMIT = 20;
/** Payments listed under the QR; older ones are in the activity. */
export const INCOMING_PAYMENTS_SHOWN = 5;

export interface BrebKeyIncomingPayments {
  /** Newest first, at most `INCOMING_PAYMENTS_SHOWN`. */
  payments: BrebKeyIncomingPayment[];
  /** How many arrived since the QR opened: each one rings, even one that lands behind a newer payment. */
  arrivals: number;
  /** The one that arrived last, which is not always the newest (a push can be lost and polled later). */
  lastArrival: BrebKeyIncomingPayment | null;
}

const NOTHING_YET: BrebKeyIncomingPayments = { payments: [], arrivals: 0, lastArrival: null };

interface ReceivedState extends BrebKeyIncomingPayments {
  keyId: string;
}

/**
 * The payments that reached a key while its any-amount QR is on screen, newest first, so whoever is showing
 * the QR sees the money land instead of asking the payer. Nothing that arrived before the QR opened is
 * listed.
 *
 * Two channels, like `usePayinLiveStatus`:
 *
 * - **Socket** — `payin:updated` on the notifications singleton. A credited Bre-B payin of the organization is
 *   read once (`GET /payins/:id`), because only the detail says which key and channel it came through.
 * - **Polling fallback** — the newest credited Bre-B payins every 10 s while the tab is visible, and at once
 *   when it becomes visible again. The first answer is the baseline: everything in it predates the QR on
 *   screen and is never announced. Later ones are read like a push.
 *
 * A payin is read once whichever channel brings it; a failed read is forgotten so the next poll retries it. A
 * push the backend marks `announce: false` (the pesos of a USDT sale reaching the key) is never listed: to
 * whoever is showing the QR it would read as a customer's payment. Pass `null` while there is no QR to watch.
 * A different key starts over.
 */
export function useBrebKeyIncomingPayments(
  receivingKey: Pick<BrebReceivingKey, "id" | "key_value"> | null,
  organizationId?: string,
): BrebKeyIncomingPayments {
  const { user, isInternalUser } = useAuth();
  // Scoped like `useBrebReceivingKey`: members are pinned server-side and never send the organization; staff
  // must, or the list would span every organization, and they also receive every organization's pushes.
  const scopedOrgId = isInternalUser
    ? (organizationId ?? user?.organization_id ?? user?.organization?.id ?? null)
    : null;
  const canWatch = !isInternalUser || !!scopedOrgId;
  const keyId = receivingKey?.id ?? null;
  const keyValue = receivingKey?.key_value ?? null;
  const [received, setReceived] = useState<ReceivedState | null>(null);

  useEffect(() => {
    if (!keyId || !keyValue || !canWatch) return;
    const key = { id: keyId, key_value: keyValue };
    const seen = new Set<string>();
    let active = true;
    let baselined = false;
    let polling = false;

    const inspect = (payinId: string) => {
      if (seen.has(payinId)) return;
      seen.add(payinId);
      PaymentsPayinService.getPayin(payinId)
        .then(({ data }) => {
          const payment = active ? incomingPaymentFor(data, key) : null;
          if (!payment) return;
          // Told here: another tab's corner toast stays quiet for it.
          markAnnounced(announcementIdFor("payins_breb_key", payment.id));
          setReceived((current) => {
            const before = current?.keyId === keyId ? current : null;
            return {
              keyId,
              payments: withIncomingPayment(before?.payments ?? [], payment, INCOMING_PAYMENTS_SHOWN),
              arrivals: (before?.arrivals ?? 0) + 1,
              lastArrival: payment,
            };
          });
        })
        .catch(() => {
          seen.delete(payinId);
        });
    };

    const poll = () => {
      if (!active || polling || document.hidden) return;
      polling = true;
      PaymentsPayinService.listPayins({
        organizationId: scopedOrgId,
        rail: PayinRail.BREB,
        status: PayinRuntimeStatus.CONFIRMED,
        currency: "COP",
        limit: INCOMING_POLL_LIMIT,
      })
        .then(({ data }) => {
          if (!active) return;
          if (!baselined) {
            baselined = true;
            data.data.forEach((payin) => seen.add(payin.id));
            return;
          }
          data.data.forEach((payin) => inspect(payin.id));
        })
        .catch(() => {
          // A failed read keeps whatever is on screen; the next tick asks again.
        })
        .finally(() => {
          polling = false;
        });
    };

    // While this QR is on screen, the corner toast leaves its payments to the notice above it.
    const offScreen = showOnScreen(screenTokens.brebKey(key));
    const unsubscribe = notificationsSocketService.onPayinUpdate((payload) => {
      // Remembered as seen, so the poll does not list it either.
      if (payload.announce === false) seen.add(payload.id);
      else if (isIncomingPaymentCandidate(payload, scopedOrgId)) inspect(payload.id);
    });
    const onVisible = () => {
      if (!document.hidden) poll();
    };

    poll();
    const timer = setInterval(poll, INCOMING_POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      offScreen();
      unsubscribe();
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [keyId, keyValue, canWatch, scopedOrgId]);

  return received?.keyId === keyId ? received : NOTHING_YET;
}
